
import Vue from "vue";
import { Component, Prop } from "vue-property-decorator";

import { pipe } from "fp-ts/lib/function";
import { fold } from "fp-ts/lib/Either";
import defensiveCopy from "@/util/defensive-copy";
import {
  DEFAULT_LINK_VALUE,
  LinkInputValue,
  LinkInputValueV
} from "@/types/link";

import { isDynamicShortcode } from "@/lib/dynamic-data/utils";
import DynamicDataChooser from "@/components/Reusable/ControlsTree/DynamicData/DynamicDataChooser.vue";
import { isFutureLayerMode } from "@/futurelayer/ui/ui";

import TextInput from "@/components/Reusable/CustomInputs/TextInput.vue";
import DynamicInput from "@/components/Reusable/CustomInputs/DynamicInput.vue";
import WPMediaInput from "@/components/Reusable/CustomInputs/MediaInput/WPMediaInput.vue";
import LinkInputSettings from "@/components/Reusable/CustomInputs/LinkInput/LinkInputSettings.vue";
import LinkChooserDialog from "@/components/Reusable/CustomInputs/LinkInput/LinkChooserDialog.vue";
import Icon from "@/components/UI/Icon.vue";
import { DynamicFieldOutput } from "@/types/dynamic-data";
import { iotsErrorsToCustomErrorType } from "@/util/io-ts-errors-to-_error";

@Component({
  components: {
    Icon,
    LinkChooserDialog,
    LinkInputSettings,
    WPMediaInput,
    TextInput,
    DynamicInput,
    DynamicDataChooser
  }
})
export default class LinkInput extends Vue {
  @Prop()
  value?: LinkInputValue;

  @Prop({
    default: () => ({
      hideAdvanced: false
    })
  })
  options!: {
    hideAdvanced?: boolean;
  };

  settingsOpen = false;
  dialogOpen = false;

  get internalValue(): any {
    // For backwards compatibility.
    // Turn string values into link objects.
    if (typeof this.value === "string") {
      return { type: "url", url: this.value };
    }

    return pipe(
      LinkInputValueV.decode(this.value),
      fold(
        err => {
          iotsErrorsToCustomErrorType(err);
          return DEFAULT_LINK_VALUE;
        },
        link => link
      )
    );
  }

  get expanded() {
    return this.settingsOpen;
  }

  get isUrlDynamic() {
    return isDynamicShortcode(this.internalValue.url);
  }

  get isFutureLayerMode() {
    return isFutureLayerMode();
  }

  updateUrl(url: string | null) {
    if (!url) {
      this.emit(null);
      return;
    }

    const newValue = defensiveCopy(this.internalValue);
    const newUrl = url.trim().replace(/<(br\s+)?(br)?>/gi, "");
    this.emit({ ...newValue, url: newUrl });
  }

  onDynamicDataSelected(value: DynamicFieldOutput) {
    const newValue = defensiveCopy(this.internalValue);
    this.emit({ ...newValue, url: value.shortcode, dynamicMeta: value });
  }

  clearDynamicValue() {
    const newValue = defensiveCopy(this.internalValue);
    this.emit({ ...newValue, url: "", dynamicMeta: undefined });
  }

  onLinkSelected(url: string) {
    const newValue = defensiveCopy(this.internalValue);
    this.emit({ ...newValue, type: "url", url, dynamicMeta: undefined });
  }

  toggleSettings() {
    this.settingsOpen = !this.settingsOpen;
  }

  openDialog() {
    this.dialogOpen = true;
  }

  closeDialog() {
    this.dialogOpen = false;
  }

  emit(newValue: LinkInputValue | null) {
    this.$emit("input", newValue);
  }
}
